import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  DataTable,
  Select,
  Button,
  Skeleton,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { CurrencyCode } from '@bbpos/types';
import { productService } from '@/services/product.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import type { ValuationItem, ValuationResponse } from '@/types/product';

type ValuationRow = ValuationItem & { id: string };

// Page size used when fetching the *full* dataset for CSV export (server caps at 200).
const EXPORT_PAGE_SIZE = 200;

// Bucket the valuation report uses for products with no category
// (COALESCE(p.category, 'Uncategorised') in products.repository.ts). Must match the
// backend string exactly — including the British 's' — for the filter to work.
const UNCATEGORISED_BUCKET = 'Uncategorised';

// ─── Category filter options ─────────────────────────────────────────────────

/**
 * Build the Category dropdown options. getCategories() omits NULL categories, but the
 * valuation table groups and filters those products under 'Uncategorised', so the
 * dropdown must offer that bucket explicitly — otherwise a manager can never isolate
 * the no-category products (GitHub issue #222). The option's value matches the exact
 * backend string so `?category=Uncategorised` actually filters.
 */
export function buildCategoryOptions(categories: string[]): { value: string; label: string }[] {
  const options = [
    { value: '', label: 'All Categories' },
    ...categories.map((c) => ({ value: c, label: c })),
  ];
  if (!categories.includes(UNCATEGORISED_BUCKET)) {
    options.push({ value: UNCATEGORISED_BUCKET, label: UNCATEGORISED_BUCKET });
  }
  return options;
}

// ─── CSV Escaping ────────────────────────────────────────────────────────────

function escapeCsvField(value: string | number | undefined): string {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// ─── CSV Export (whole filtered dataset) ─────────────────────────────────────

/**
 * Fetch every product in the current filter by paging through the valuation
 * report, not just the one page shown on screen. See GitHub issue #221: the
 * export previously used only the current page (≤20 rows), so its Total Value
 * sum could never reconcile with the dataset-wide Total Valuation KPI.
 */
export async function fetchAllValuationItems(
  fetchPage: (page: number) => Promise<ValuationResponse>,
): Promise<ValuationItem[]> {
  const first = await fetchPage(1);
  const totalPages = Math.max(1, first.meta?.totalPages ?? 1);
  const items = [...first.data];
  for (let page = 2; page <= totalPages; page++) {
    const next = await fetchPage(page);
    items.push(...next.data);
  }
  return items;
}

/** Build the inventory-valuation CSV from a list of items (monetary values in minor units). */
export function buildValuationCsv(items: ValuationItem[], currency: CurrencyCode = 'INR'): string {
  const header = 'Product,SKU,Category,Quantity,Unit,WAC per Unit,Total Value\n';
  const rows = items
    .map((p) =>
      [
        escapeCsvField(p.name),
        escapeCsvField(p.sku),
        escapeCsvField(p.category),
        p.stock_quantity,
        escapeCsvField(p.unit),
        (p.wac_per_unit / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)),
        (p.total_value / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)),
      ].join(','),
    )
    .join('\n');
  return header + rows;
}

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiConfig {
  title: string;
  value: string;
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
  tintBg: string;
  darkTintBg: string;
}

function KpiCard({ title, value, icon, gradient, iconBg, tintBg, darkTintBg }: KpiConfig) {
  return (
    <div className={`relative overflow-hidden rounded-xl ${tintBg} ${darkTintBg} shadow-md hover:shadow-lg transition-all duration-200 p-5 group animate-slide-up`}>
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full ${gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
      </div>
    </div>
  );
}

// ─── Table Columns ───────────────────────────────────────────────────────────

function makeColumns(currency: CurrencyCode): ColumnDef<ValuationRow>[] {
  return [
  {
    key: 'name',
    header: 'Product',
    render: (_v, row) => (
      <div>
        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.name}</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">{row.sku}</p>
      </div>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    render: (_v, row) => (
      <span className="text-sm text-neutral-600 dark:text-neutral-300">{row.category || '\u2014'}</span>
    ),
  },
  {
    key: 'stock_quantity',
    header: 'Quantity',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
        {Number(row.stock_quantity).toLocaleString('en-IN')} {row.unit}
      </span>
    ),
  },
  {
    key: 'wac_per_unit',
    header: 'WAC / Unit',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 tabular-nums">
        {formatPaise(row.wac_per_unit, currency)}
      </span>
    ),
  },
  {
    key: 'total_value',
    header: 'Total Value',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
        {formatPaise(row.total_value, currency)}
      </span>
    ),
  },
  ];
}

function InventoryValuationContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currency = useTenantCurrency();
  const { toast } = useToast();
  const columns = useMemo(() => makeColumns(currency), [currency]);
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const page = Number(searchParams.get('page') ?? '1');
  const categoryFilter = searchParams.get('category') ?? '';

  // Scope the category dropdown to the selected location so it only offers
  // categories that actually have products there. The valuation query below is
  // location-scoped; a tenant-wide category list would let a manager pick a
  // category empty at this location and get an unexplained blank table (#223).
  const { data: categoriesData } = useQuery({
    queryKey: ['product-categories', selectedLocationId],
    queryFn: () => productService.getCategories(selectedLocationId),
    staleTime: 60_000,
  });

  const categoryOptions = buildCategoryOptions(categoriesData ?? []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory-valuation', selectedLocationId, page, categoryFilter],
    queryFn: () =>
      productService.getValuation({
        location_id: selectedLocationId,
        page,
        limit: 20,
        category: categoryFilter || undefined,
      }),
    staleTime: 30_000,
  });

  function setFilter(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  }

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = useCallback(async () => {
    if (!data?.summary?.total_skus || isExporting) return;
    setIsExporting(true);
    try {
      // Export the entire filtered dataset (every page), not just the current page,
      // so the CSV's Total Value sum reconciles with the Total Valuation KPI (#221).
      const items = await fetchAllValuationItems((page) =>
        productService.getValuation({
          location_id: selectedLocationId,
          page,
          limit: EXPORT_PAGE_SIZE,
          category: categoryFilter || undefined,
        }),
      );
      const blob = new Blob([buildValuationCsv(items, currency)], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory-valuation-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Export failed', description: 'Could not load the full valuation report. Please try again.', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [data, isExporting, selectedLocationId, categoryFilter, currency, toast]);

  const valuationItems: ValuationRow[] = (data?.data ?? []).map((item) => ({ ...item, id: item.id }));

  if (error) {
    return (
      <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 shadow-md p-4 text-sm text-danger-700 dark:text-danger-300">
        Failed to load valuation data. Please try refreshing.
      </div>
    );
  }

  if (isLoading) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-teal-600 to-success-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Inventory Valuation</h1>
            <p className="text-teal-100 mt-1">Current stock value at Weighted Average Cost</p>
          </div>
          <Button
            variant="secondary"
            onClick={handleExportCsv}
            disabled={!data?.summary?.total_skus || isExporting}
            className="bg-white/20 hover:bg-white/30 text-white border-white/30"
          >
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <KpiCard
          title="Total Valuation"
          value={formatPaise(data?.summary?.total_value ?? 0, currency)}
          gradient="bg-gradient-to-br from-success-500 to-success-700"
          iconBg="bg-gradient-to-br from-success-500 to-success-600"
          tintBg="bg-success-50"
          darkTintBg="dark:bg-success-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <KpiCard
          title="Total SKUs"
          value={(data?.summary?.total_skus ?? 0).toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-teal-500 to-teal-700"
          iconBg="bg-gradient-to-br from-teal-500 to-teal-600"
          tintBg="bg-teal-50"
          darkTintBg="dark:bg-teal-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>}
        />
        <KpiCard
          title="Avg Cost / Unit"
          value={formatPaise(data?.summary?.avg_cost_per_unit ?? 0, currency)}
          gradient="bg-gradient-to-br from-indigo-500 to-indigo-700"
          iconBg="bg-gradient-to-br from-indigo-500 to-indigo-600"
          tintBg="bg-indigo-50"
          darkTintBg="dark:bg-indigo-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>}
        />
        {/* Goods dispatched but not yet received — in neither branch's stock, so the
            three KPIs above + this one reconcile to the pre-transfer total. */}
        <KpiCard
          title="In Transit"
          value={formatPaise(data?.summary?.in_transit_value ?? 0, currency)}
          gradient="bg-gradient-to-br from-brand-500 to-brand-700"
          iconBg="bg-gradient-to-br from-brand-500 to-brand-600"
          tintBg="bg-brand-50"
          darkTintBg="dark:bg-brand-900/20"
          icon={<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="w-full sm:w-48">
          <Select
            options={categoryOptions}
            value={categoryFilter}
            onChange={(v) => setFilter('category', typeof v === 'string' ? v : '')}
            placeholder="Category"
          />
        </div>
      </div>

      {/* Table */}
      <div className="animate-slide-up">
        <DataTable<ValuationRow>
          columns={columns}
          data={valuationItems}
          loading={isLoading}
          totalCount={data?.meta?.total}
          currentPage={page}
          pageSize={20}
          onPageChange={(p) => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(p));
            setSearchParams(params);
          }}
          emptyState={{
            title: 'No valuation data available',
            description: 'Add products with stock to see valuation',
          }}
        />
      </div>
    </>
  );
}

export function InventoryValuationPage() {
  return (
    <PageContainer
      title="Inventory Valuation"
      subtitle="Current stock value at Weighted Average Cost"
      breadcrumbs={[{ label: 'Inventory', href: '/inventory' }, { label: 'Valuation' }]}
    >
      <ErrorBoundary resetKey="inventory-valuation">
        <InventoryValuationContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
